import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Clock, Lock, PackageCheck, RotateCcw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { rpcTriagem } from '@/components/kanban/triagem/rpcTriagem';
import { TriagemPedidoFechadoModal } from '@/components/kanban/fechado/TriagemPedidoFechadoModal';
import { TriagemCriacaoPedidoModal } from '@/components/kanban/criacao/TriagemCriacaoPedidoModal';
import { TriagemFluxoModal } from '@/components/kanban/fluxo/TriagemFluxoModal';
import { TRIAGENS_FLUXO } from '@/components/kanban/fluxo/triagensFluxoConfig';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useContratoAdicional } from '@/hooks/useFuncoesAdicionais';

/* CICLO DE 2 HORAS (Douglas 27/10/2026, maquete aprovada; antes 1 hora, de
   15/10). O pedido CNPJ chega com dono no Pedido fechado. A cada hora (8h–18h,
   dia útil), o Aguardando HP dele sem tratar há 2 h e o Pedido fechado, a
   Criação e o Pedido recuperado parados há 2 h travam as outras filas, o HP
   primeiro: consultar é rápido e o que liberou é inputado na hora. A tela não
   fala em dinheiro: a comissão do BKO está suspensa (Douglas 24/10/2026). Quem
   decide é fn_bko_contrato_travado; o painel só aparece travado e só abre as
   triagens que já existem. */

type PedidoTrava = {
  pedido_id: string; numero_pedido: string | null; coluna: string | null;
  grupo?: 'hp' | 'contrato'; produto?: string | null; estourou_min?: number | null;
};
type Trava = {
  aplica: boolean; travado: boolean; travado_em?: string | null; proximo_check?: string | null; prazo_horas?: number;
  pedidos_trava?: PedidoTrava[]; travas_hoje?: number; pedido_fechado?: number; criacao?: number; cpf?: number;
};
type Aberta = 'hp' | 'criacao' | 'fechado' | 'recuperado' | null;

const TRAVADOS = ['Biometria', 'Aceite', 'Input', 'Kanban', 'Inbox'];

const NOME_COLUNA: Record<string, string> = {
  'CRIAÇÃO PEDIDO': 'Criação',
  'PEDIDO FECHADO': 'Pedido fechado',
  'PEDIDO RECUPERADO': 'Recuperado',
};

function estourouHa(min: number | null | undefined) {
  const m = Math.max(0, Math.round(min ?? 0));
  if (m === 0) return 'venceu agora';
  if (m < 60) return `atrasado há ${m} min`;
  const h = Math.floor(m / 60);
  const resto = m % 60;
  return `atrasado há ${h}h${resto ? String(resto).padStart(2, '0') : ''}`;
}

const BTN_PRIMARIO = 'flex h-11 items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#4A1320,#5B0A0D)] text-[12.5px] font-bold text-white transition hover:brightness-110 dark:bg-[linear-gradient(135deg,#9D2530,#6C2236)]';
const BTN_GHOST = 'flex h-11 items-center justify-center gap-2 rounded-lg border border-border bg-card text-[12.5px] font-bold text-foreground transition-colors hover:border-primary/50 hover:text-primary';

export function BkoContratoPrimeiroACriacao() {
  const { data: perfil } = useUserProfile();
  const { principal, adicional } = useContratoAdicional();
  const ehBkoContrato = principal || adicional;
  const [aberta, setAberta] = useState<Aberta>(null);
  const q = useQuery({
    queryKey: ['bko-contrato-travado', perfil?.id],
    enabled: ehBkoContrato,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Trava> => {
      const { data, error } = await rpcTriagem('fn_bko_contrato_travado', {});
      if (error) throw new Error(error.message || 'Não deu para conferir a fila da manhã.');
      return data as Trava;
    },
  });
  if (!ehBkoContrato || !q.data?.travado) return null;
  const t = q.data;
  const pedidosTrava = t.pedidos_trava ?? [];
  const prazo = t.prazo_horas ?? 2;
  const hp = pedidosTrava.filter((p) => p.grupo === 'hp');
  const contrato = pedidosTrava.filter((p) => p.grupo !== 'hp');
  const temRecuperado = contrato.some((p) => p.coluna === 'PEDIDO RECUPERADO');
  const fechar = () => { setAberta(null); q.refetch(); };

  const grupos: { chave: string; titulo: string; acao: string; pedidos: PedidoTrava[]; detalhe: (p: PedidoTrava) => string }[] = [];
  if (hp.length) grupos.push({
    chave: 'hp', titulo: `Aguardando HP · sem tratar há ${prazo} h`, acao: 'Inputar, justificar ou cancelar', pedidos: hp,
    detalhe: (p) => [p.produto, estourouHa(p.estourou_min)].filter(Boolean).join(' · '),
  });
  if (contrato.length) grupos.push({
    chave: 'contrato', titulo: `Criação · parado há ${prazo} h`, acao: temRecuperado ? 'Criar ou retomar' : 'Criar', pedidos: contrato,
    detalhe: (p) => `${NOME_COLUNA[p.coluna ?? ''] ?? p.coluna ?? '-'} · ${estourouHa(p.estourou_min)}`,
  });

  const botoes: { chave: Exclude<Aberta, null>; rotulo: string; Icone: LucideIcon }[] = [
    { chave: 'hp', rotulo: 'Triagem Aguardando HP', Icone: Clock },
    { chave: 'criacao', rotulo: 'Triagem Criação', Icone: ClipboardList },
    { chave: 'fechado', rotulo: 'Triagem Pedido Fechado', Icone: PackageCheck },
    ...(temRecuperado ? [{ chave: 'recuperado' as const, rotulo: 'Triagem Pedido Recuperado', Icone: RotateCcw }] : []),
  ];
  const primario = hp.length ? 'hp' : 'criacao';

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="primeiro-a-criacao-titulo"
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-background/95 p-4 backdrop-blur-[2px]">
      <div className="w-full max-w-[600px] overflow-hidden rounded-xl border border-border-warm bg-card shadow-[0_1px_2px_rgba(60,20,30,0.05),0_4px_14px_-6px_rgba(60,20,30,0.08)]">
        <div className="bg-[linear-gradient(150deg,#5E1424,#3E0E1B)] px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-white/60">BKO Contrato</p>
          <h2 id="primeiro-a-criacao-titulo" className="mt-1 font-display text-[22px] font-bold">Ciclo de {prazo} horas: trate os pedidos parados</h2>
          <p className="mt-1 text-[12.5px] text-white/85">
            {hp.length
              ? 'Comece pelo Aguardando HP: consultar é rápido e o que liberou vai para input na hora. Depois crie os da Criação.'
              : `Você tinha pedido parado há mais de ${prazo} horas. Crie estes pedidos e o CRM libera o resto.`}
          </p>
          <div className="mt-3 flex items-baseline gap-3">
            <p className="font-display text-[34px] font-bold leading-none tabular-nums">Faltam {pedidosTrava.length}</p>
            <p className="text-[12px] text-white/60 tabular-nums">
              {[hp.length ? `${hp.length} no Aguardando HP` : '', contrato.length ? `${contrato.length} na Criação` : ''].filter(Boolean).join(' · ')}
            </p>
          </div>
        </div>
        <div className="border-b border-border px-6 py-3">
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Pedidos da trava</p>
            <p className="text-[11px] text-muted-foreground">Travas hoje: <span className="font-bold tabular-nums text-primary">{t.travas_hoje ?? 0}</span></p>
          </div>
          <div className="max-h-[240px] overflow-y-auto">
            {grupos.map((g, i) => (
              <div key={g.chave} className="mt-2 first:mt-0">
                <div className="flex h-7 items-center justify-between bg-[#FAF8F4] px-2 dark:bg-[#241A1A]">
                  <span className="truncate text-[10px] font-bold uppercase tracking-[1.2px] text-foreground">{i + 1}º · {g.titulo}</span>
                  <span className="shrink-0 text-[10.5px] text-muted-foreground">{g.acao}</span>
                </div>
                {g.pedidos.map((p) => (
                  <div key={p.pedido_id} className="flex h-7 items-center justify-between gap-3 border-t border-border px-2 text-[12.5px]">
                    <span className="shrink-0 font-semibold tabular-nums text-foreground">{p.numero_pedido || '-'}</span>
                    <span className="truncate text-[11px] text-muted-foreground" title={g.detalhe(p)}>{g.detalhe(p)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-3 px-6 py-4">
          <div className={`grid gap-3 ${botoes.length === 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {botoes.map((b) => (
              <button key={b.chave} type="button" onClick={() => setAberta(b.chave)}
                className={b.chave === primario ? BTN_PRIMARIO : BTN_GHOST}>
                <b.Icone className="h-4 w-4 shrink-0" /> <span className="truncate">{b.rotulo}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-nowrap items-center gap-1.5 overflow-hidden border-t border-border pt-3">
            <span className="shrink-0 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Travados</span>
            {TRAVADOS.map((n) => (
              <span key={n} className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[10.5px] text-muted-foreground">
                <Lock className="h-3 w-3" /> {n}
              </span>
            ))}
          </div>
        </div>
      </div>
      {aberta === 'hp' && <TriagemFluxoModal config={TRIAGENS_FLUXO['AGUARDANDO HP']} isOpen onClose={fechar} />}
      <TriagemPedidoFechadoModal isOpen={aberta === 'fechado'} onClose={fechar} />
      <TriagemCriacaoPedidoModal isOpen={aberta === 'criacao'} onClose={fechar} />
      {aberta === 'recuperado' && <TriagemFluxoModal config={TRIAGENS_FLUXO['PEDIDO RECUPERADO']} isOpen onClose={fechar} />}
    </div>
  );
}
